/**
 * Every party committee in the record, sortable and searchable.
 *
 * This closes the page. The four cards above are the committees a lobbyist's
 * LD-203 names; this is the other 242 as well — every state and county party
 * that moved money to a candidate, plus the DNC and RNC — and it is the only
 * way a reader finds the party in their own state.
 *
 * Two things it will not do. There is no total column and no total row: the
 * four money-out columns are four different acts, $411.4m of the money is
 * advertising bought to attack the candidate it names, and a figure summed
 * across them would be wrong about the amount, the direction and the
 * recipient at once. And every amount is written with the direction it was
 * pointing under it, not just in the header, because a reader who lands
 * mid-table and reads one cell must still see which way the money went.
 *
 * Filter state lives in the query string, as on the members and laws pages,
 * so a search can be linked and the back button behaves.
 */

import { Link, useSearchParams } from "react-router-dom";
import { api, type PartyCommittee } from "../api";
import { useAsync, useDebounced } from "../useAsync";
import { Problem } from "./bits";
import { NAMED, alsoHeld, figure, nonFederal, resolvedMoneyIn } from "./committees";
import {
  DEFAULT_SORT,
  PER_PAGE,
  columnsFor,
  orderWords,
  pageFrom,
  resolve,
  sortFrom,
  sortParam,
  toggled,
  type Column,
  type Sort,
  type View,
} from "./committeeIndex";

export function CommitteeTable({ held }: { held: PartyCommittee[] }) {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";
  const sort = sortFrom(params.get("sort"), params.get("dir"));
  const page = pageFrom(params.get("page"));
  const settled = useDebounced(query);

  // Asked of the endpoint with the parameters it is growing. Until they are
  // live this comes back as the whole index and `resolve` discards it in
  // favour of `held`, which is the same payload the cards above are drawn
  // from — so the table is right either way, and is drawn on the first paint
  // rather than after a round trip.
  const answered = useAsync(
    () =>
      api.partyCommittees({
        q: settled || undefined,
        sort: sortParam(sort),
        page,
        per_page: PER_PAGE,
      }),
    [settled, sort.key, sort.way, page],
  );

  const columns = columnsFor(held.length > 0 ? held : (answered.data?.committees ?? []));
  const view = resolve(answered.data, held, sort, settled, page);

  function set(changes: Record<string, string>) {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    if (!("page" in changes)) next.delete("page");
    setParams(next, { replace: true });
  }

  const others = alsoHeld(held).length;
  const resolved = resolvedMoneyIn(held);
  const filtered = query.trim().length > 0;
  const touched = filtered || sort.key !== DEFAULT_SORT.key || sort.way !== DEFAULT_SORT.way;

  return (
    <section className="mt-14">
      <h2 className="font-display text-2xl font-semibold">Every party committee in the record</h2>
      <p className="mt-1.5 max-w-3xl text-muted">
        The four above are shown apart because they are the four the LD-203 record could also name.
        The same FEC record holds every state and county party that moved money to a candidate,
        plus the DNC and RNC
        {others > 0 && <> — {others.toLocaleString()} committees beyond the four</>}. Lobbyists
        disclose giving to these too
        {resolved.named > 0 && (
          <>
            : {resolved.named.toLocaleString()} of them are named on cheques worth{" "}
            {figure(resolved.amount)}, and {resolved.dashed.toLocaleString()} show a dash
          </>
        )}
        . A dash is money we have not resolved rather than a committee that was given nothing — the
        LD-203 names its recipient in free text, and a name that does not land on exactly one
        committee in the FEC&rsquo;s register is left unresolved rather than guessed at. They spend,
        and dropping them would understate what the parties do.
      </p>

      <div className="mt-6 flex flex-wrap gap-3">
        <input
          value={query}
          onChange={(event) => set({ q: event.target.value })}
          type="search"
          aria-label="Search party committees by name"
          placeholder="Search by committee name…"
          className="min-w-64 flex-1 rounded-xl border border-line bg-card px-4 py-2.5 shadow-sm placeholder:text-faint"
        />
        {touched && (
          <button
            type="button"
            onClick={() => setParams(new URLSearchParams(), { replace: true })}
            className="rounded-xl border border-line px-4 py-2.5 text-sm font-medium text-muted hover:text-ink"
          >
            Clear
          </button>
        )}
      </div>

      <p className="mt-4 text-sm text-muted" role="status">
        <strong className="tnum font-semibold text-ink">{view.total.toLocaleString()}</strong>{" "}
        {view.total === 1 ? "committee" : "committees"}
        {filtered && (view.total === 1 ? " matches" : " match")} · ordered by{" "}
        <span className="text-ink">{orderWords(sort)}</span>
        {view.pages > 1 && (
          <>
            {" "}
            · page <span className="tnum">{view.page}</span> of{" "}
            <span className="tnum">{view.pages}</span>
          </>
        )}
      </p>

      {/* The parameterised request failing is not a failure of this section
          while there is an index to draw from — the reader gets the table,
          sorted here instead. It is only worth reporting when there is
          nothing left to show. */}
      {answered.error && view.rows.length === 0 && (
        <div className="mt-6">
          <Problem error={answered.error} retry={answered.reload} />
        </div>
      )}

      {view.rows.length === 0 && !answered.error && <Nothing query={query} />}

      {view.rows.length > 0 && (
        <>
          <div className="mt-4 overflow-x-auto rounded-2xl border border-line bg-card shadow-sm">
            <table className="w-full min-w-4xl text-sm">
              <caption className="sr-only">
                Party committees, ordered by {orderWords(sort)}. Money out is given by category and
                is never totalled.
              </caption>
              <thead>
                <tr className="border-b border-line bg-sunk/70 text-left align-bottom">
                  {columns.map((column) => (
                    <Header
                      key={column.key}
                      column={column}
                      sort={sort}
                      onSort={(next) => set({ sort: next.key, dir: next.way })}
                    />
                  ))}
                </tr>
              </thead>
              <tbody className={`divide-y divide-line ${answered.loading ? "opacity-70" : ""}`}>
                {view.rows.map((committee) => (
                  <Row key={committee.committee_id} committee={committee} columns={columns} />
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-3 max-w-3xl text-sm text-faint">
            Read down a column, not across a row. The four money-out columns are four different
            things — a cheque the campaign banked, a buy made with it, an advertisement for a
            candidate and an advertisement against one — so no row here carries a total, and the
            attacking column is money aimed at a candidate rather than money they received.
          </p>

          <Pager view={view} onPage={(next) => set({ page: String(next) })} />
        </>
      )}
    </section>
  );
}

/** A sortable column heading.
 *
 *  `aria-sort` on the cell as well as the arrow beside the label, so which
 *  column is ordering the table and which way is not carried by a glyph
 *  alone. */
function Header({
  column,
  sort,
  onSort,
}: {
  column: Column;
  sort: Sort;
  onSort: (next: Sort) => void;
}) {
  const on = sort.key === column.key;
  const against = column.facts?.direction === "against";
  return (
    <th
      scope="col"
      aria-sort={on ? (sort.way === "asc" ? "ascending" : "descending") : "none"}
      className={`p-0 font-medium ${on ? "bg-brand-soft/60" : against ? "bg-gop/5" : ""}`}
    >
      <button
        type="button"
        onClick={() => onSort(toggled(sort, column))}
        title={`${column.about}. Click to sort.`}
        className={`group flex w-full flex-col gap-0.5 px-4 py-3 transition hover:bg-sunk ${
          column.numeric ? "items-end text-right" : "items-start text-left"
        }`}
      >
        <span className="flex items-baseline gap-1 whitespace-nowrap">
          <span className={on ? "text-ink" : "text-muted"}>{column.label}</span>
          <span
            aria-hidden="true"
            className={`text-xs ${
              on ? "text-brand" : "text-faint opacity-0 transition group-hover:opacity-100"
            }`}
          >
            {on && sort.way === "asc" ? "↑" : "↓"}
          </span>
        </span>
        {column.facts && (
          <span
            className={`text-[10px] font-semibold tracking-wide uppercase ${
              against ? "text-gop" : "text-faint"
            }`}
          >
            {column.facts.tag}
          </span>
        )}
      </button>
    </th>
  );
}

function Row({ committee, columns }: { committee: PartyCommittee; columns: Column[] }) {
  return (
    <tr className="transition hover:bg-sunk">
      {columns.map((column) => {
        if (column.key === "name") return <Name key={column.key} committee={committee} />;
        if (column.key === "money_in") return <MoneyIn key={column.key} committee={committee} />;
        if (column.key === "transactions") {
          return (
            <td key={column.key} className="tnum px-4 py-2.5 text-right text-muted">
              {committee.transactions.toLocaleString()}
            </td>
          );
        }
        return <Spent key={column.key} column={column} committee={committee} />;
      })}
    </tr>
  );
}

/** The row's header cell, so a screen reader announces which committee an
 *  amount belongs to. The four acronyms carry whose money they are: the FEC
 *  files "DSCC" and nothing more, and the two that differ by one letter
 *  differ by chamber. */
function Name({ committee }: { committee: PartyCommittee }) {
  const named = NAMED[committee.name.trim().toUpperCase()];
  return (
    <th scope="row" className="px-4 py-2.5 text-left font-normal">
      <Link
        to={`/money/committees/${committee.committee_id}`}
        className="font-medium hover:text-brand"
      >
        {committee.name}
      </Link>
      {named && <span className="ml-2 text-xs text-faint">{named.whose}</span>}
    </th>
  );
}

/** What lobbyists disclosed giving, and — for the few committees that hold
 *  them — what went to an account no candidate can be paid from. Shown under
 *  the federal figure rather than added to it. */
function MoneyIn({ committee }: { committee: PartyCommittee }) {
  const held = nonFederal(committee);
  if (!committee.money_in_federal && !held) {
    return (
      <td
        className="px-4 py-2.5 text-right text-faint"
        title="No LD-203 disclosure resolved to this committee"
      >
        —
      </td>
    );
  }
  return (
    <td className="px-4 py-2.5 text-right">
      <span className="tnum font-medium">{figure(committee.money_in_federal)}</span>
      <span className="block text-[10px] tracking-wide text-faint uppercase">
        {committee.gifts.toLocaleString()} {committee.gifts === 1 ? "cheque" : "cheques"}
      </span>
      {held > 0 && (
        <span className="block text-[10px] tracking-wide text-faint uppercase">
          +{figure(held)} non-federal
        </span>
      )}
    </td>
  );
}

/** One category of money out, with the direction it was pointing under the
 *  amount.
 *
 *  The tag is repeated on every cell rather than left to the header. It is
 *  the same thing the cards do above, and the reason is the same: "$12.3m"
 *  in a cell beside a committee's name reads as money that committee handed
 *  out, and in the attacking column that is the one reading it must not
 *  have. Nothing filed shows a dash — a claim about the record, not a zero. */
function Spent({ column, committee }: { column: Column; committee: PartyCommittee }) {
  const amount = Number(column.of(committee));
  const against = column.facts?.direction === "against";
  const cell = against ? "px-4 py-2.5 text-right bg-gop/5" : "px-4 py-2.5 text-right";
  if (!amount) return <td className={`${cell} text-faint`}>—</td>;
  return (
    <td className={cell}>
      <span className={`tnum font-medium ${against ? "text-gop" : ""}`}>{figure(amount)}</span>
      <span
        className={`block text-[10px] tracking-wide uppercase ${
          against ? "text-gop/85" : "text-faint"
        }`}
      >
        {column.facts?.tag}
      </span>
    </td>
  );
}

function Nothing({ query }: { query: string }) {
  return (
    <div className="mt-6 rounded-2xl border border-line bg-card p-10 text-center">
      <p className="font-display text-xl font-semibold">No committee matched that.</p>
      <p className="mx-auto mt-2 max-w-lg text-muted">
        {query.trim() ? (
          <>
            Nothing here is named “{query.trim()}”. These are filed under the name the FEC holds, so
            a state works better than a party — “wisconsin” finds several, “democrats” finds few.
          </>
        ) : (
          <>No party committee in this corpus moved money to a candidate.</>
        )}
      </p>
    </div>
  );
}

function Pager({ view, onPage }: { view: View; onPage: (page: number) => void }) {
  if (view.pages <= 1) return null;
  return (
    <nav className="mt-8 flex items-center justify-center gap-3" aria-label="Committee pagination">
      <button
        type="button"
        disabled={view.page <= 1}
        onClick={() => onPage(view.page - 1)}
        className="rounded-lg border border-line bg-card px-4 py-2 text-sm font-medium disabled:opacity-40"
      >
        ← Previous
      </button>
      <span className="tnum text-sm text-muted">
        {view.page} / {view.pages}
      </span>
      <button
        type="button"
        disabled={view.page >= view.pages}
        onClick={() => onPage(view.page + 1)}
        className="rounded-lg border border-line bg-card px-4 py-2 text-sm font-medium disabled:opacity-40"
      >
        Next →
      </button>
    </nav>
  );
}
